import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { CurrentUser } from "../../common/providers/current-user";
import { UsersProvider } from '../../users/providers/users-provider';
import { LangsProvider } from '../../common/providers/langs-provider';
import { UsersChangePasswordDialogComponent } from '../../users/components/change-password-dialog/change-password-dialog';
import moment from 'moment';
import { AthleteRow, AthletesProvider } from '../../athletes/providers/athletes-provider';
import { OrganizationsProvider } from '../../organizations/providers/organizations-provider';
import { AddressRow } from '../../addresses/providers/addresses-provider';
import { forkJoin, Observable, of, Subscription, switchMap, throwError } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, map } from "rxjs/operators";
import { Gender } from '../../common/models/gender';
import { ContractType } from "../../common/models/contract-type";
import { User } from "../../common/models/user";
import { Lang } from "../../common/models/lang";
import { Organization } from "../../common/models/organization";
import { HasPendingChangesGuard } from "../../common/guards/has-pending-changes";
import { Athlete } from "../../common/models/athlete";
import { Address } from "../../common/models/address";
import { AddressType } from "../../common/models/address-type";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { MainActionComponent } from "../../common/components/main-action/main-action";
import { MatError, MatFormField, MatFormFieldModule, MatHint, MatLabel } from "@angular/material/form-field";
import { MobileSideMenuComponent } from "../../common/components/mobile-side-menu/mobile.side-menu.component";
import { Error404Component } from "../../common/components/error-404/error-404";
import { MobilePageContentComponent } from "../../common/components/mobile-page-content/mobile-page-content";
import { Error401Component } from "../../common/components/error-401/error-401";
import {
  MatDatepickerInput,
  MatDatepickerModule,
} from "@angular/material/datepicker";
import { MatOption } from "@angular/material/autocomplete";
import { MobileModuleContentComponent } from "../../common/components/mobile-module-content/mobile-module-content";
import { MobileHeaderComponent } from "../../common/components/mobile-header/mobile.header";
import { InputImageComponent } from "../../common/components/input-image/input-image";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatInput, MatInputModule } from "@angular/material/input";
import { NgForOf, NgIf } from "@angular/common";
import { DateClosestPipe } from "../../common/pipes/date-closest";
import { MatButton, MatButtonModule } from "@angular/material/button";

@Component({
  imports: [
    MainActionComponent,
    TranslateModule,
    MatFormFieldModule,
    MobileSideMenuComponent,
    Error404Component,
    MatError,
    MobilePageContentComponent,
    Error401Component,
    MatDatepickerModule,
    MobileModuleContentComponent,
    MobileHeaderComponent,
    InputImageComponent,
    MatRadioButton,
    MatSelectModule,
    MatRadioGroup,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    NgIf,
    DateClosestPipe,
    MatButtonModule,
    NgForOf
  ],
  providers: [],
  selector: 'mobile-user-edit-page',
  standalone: true,
  styleUrls: ['./mobile.user.edit.scss'],
  templateUrl: './mobile.user.edit.html'
})
export class MobileUsersEditPageComponent implements AfterViewInit, OnDestroy {
  public DESCRIPTION_MAX_LENGTH = 5000;
  public readonly NAME_MAX_LENGTH: number = 200;
  public readonly CHECK_EMAIL_THRESHOLD: number = 750;
  public readonly GENDER_MALE: number = Gender.ID_MALE;
  public readonly GENDER_FEMALE: number = Gender.ID_FEMALE;
  public tzNames: string[];
  // public mainActions: ModuleMenu[] = [];
  public moreMenus: any[] = [];
  public hierarchy: any[] = [];
  public pageTitle = '';
  public isLoading = true;
  public is401 = false;
  public is404 = false;
  public isError404 = false;
  public isFromModerators = false;
  public contractTypeSelected: number = ContractType.ID_DEFAULT;
  public availableGenders: Gender[] = [];
  public availableContractTypes: ContractType[] = [];

  public genderFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);
  public birthdayFormControl: FormControl = new FormControl('');
  public birthdayMinDate: Date = new Date('1900-01-01 00:00:00');
  public birthdayMaxDate: Date = new Date();
  public firstNameFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);
  public lastNameFormControl: FormControl = new FormControl('', [
    Validators.required
  ]);
  public emailFormControl: FormControl = new FormControl('', [
    Validators.required,
    Validators.email,
    // doc : https://blog.thoughtram.io/angular/2016/03/14/custom-validators-in-angular-2.html#building-a-custom-validator
    (control: any) => this.emailUnavailableError ? { unavailable: { valid: false } } : null
  ]);
  public billingEmailFormControl: FormControl = new FormControl('', [Validators.email]);
  public emailUnavailableError = false;
  public userLinkFormControls: FormControl[] = [];
  public postcodeFormControl: FormControl = new FormControl('', [
    Validators.pattern('^[0-9]*$')
  ]);
  public cityFormControl: FormControl = new FormControl('');
  public streetNameFormControl: FormControl = new FormControl('');
  public buildingNumberFormControl: FormControl = new FormControl('');
  public addressAdditionFormControl: FormControl = new FormControl('');
  public phoneNumberFormControl: FormControl = new FormControl('');
  public sizeFormControl: FormControl = new FormControl(0, [Validators.min(0)]);
  public weightFormControl: FormControl = new FormControl(0, [Validators.min(0)]);
  public sportsPracticedFormControl: FormControl = new FormControl('');
  public clubFormControl: FormControl = new FormControl('');
  public contractTypeFormControl: FormControl = new FormControl('');
  public externalCoachNameFormControl: FormControl = new FormControl('');
  public athleteDescriptionFormControl: FormControl = new FormControl('');

  public user: User = {} as User;
  public avatarImage: any = {};
  public langs: Lang[] = [];
  public userLinksData: any[] = [];
  public athleteRow: AthleteRow  = {} as AthleteRow;
  public organization: Organization = {} as Organization;
  public defaultAddressId = 0;

  private subscriptions: Subscription[] = [];
  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public usersProvider: UsersProvider,
    public utils: UtilsProvider,
    private _athletesProvider: AthletesProvider,
    private _organizationProvider: OrganizationsProvider,
    private _title: Title,
    private _router: Router,
    private _route: ActivatedRoute,
    private _langs: LangsProvider,
    private _translater: TranslateService,
    private _toaster: MatSnackBar,
    private _changesGuard: HasPendingChangesGuard,
    private _changeDetector: ChangeDetectorRef,
    private _dialoger: MatDialog,
    private api: ApiBaseService,
  ) {
    this.tzNames = moment.tz.names();
  }

  // private _originalEmail: string | null = null;
  // private _checkEmailAvailabilityRequestTimestamp: number = 0;

  @HostListener('window:beforeunload')
  public doSomething() {
    return !this._changesGuard.hasChanges();
  }

  public ngAfterViewInit() {
    this.subscriptions.push(
      this._route.params.subscribe((params) => {
        this._organizationProvider.getByCode(params['organizationCode']).pipe(
          map((organization) => {
            this.organization = organization;
            this.initPage();
          })
        ).subscribe();
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }


  public onSaveClicked(): any {
    if (!this.isFormValid()) {
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
      return of(false);
    }

    this.isLoading = true;

    // on est obligé de cloner parce que sinon l'image qui est bindée
    // va devenir un fichier au lieu d'une url dans la barre de navigation
    const user: User = this.user;
    if (this.avatarImage.new) {
      user.avatar = this.avatarImage.new;
    } else if (!this.avatarImage.current) {
      user.avatar = undefined;
    }

    return this.usersProvider.updateUser(this.user.id!, user).pipe(
      switchMap((user) => {
        this.user = user;
        /**
         * Save "athlete" data
         */
        const athleteRow: AthleteRow = {
          user: this.user,
          athlete: new Athlete({}),
          gender: {} as Gender,
          contractType: {} as ContractType,
          addresses: [],
          coaches: [],
          coachNames: '',
          teams: [],
          teamNames: '',
          rights: undefined
        };

        athleteRow.athlete.id = this.athleteRow.athlete.id;

        // athlete
        let birthdate: string | null = null;
        if (this.birthdayFormControl.value) {
          birthdate = this.birthdayFormControl.value.format ?
            this.birthdayFormControl.value.format('YYYY-MM-DD') :
            this.utils.DateToYmd(new Date(this.birthdayFormControl.value));
        }
        athleteRow.athlete.genderId = this.genderFormControl.value;
        athleteRow.athlete.contractTypeId = this.contractTypeSelected;
        athleteRow.athlete.userId = this.user.id;
        athleteRow.athlete.birthday = birthdate!;
        athleteRow.athlete.sportsPracticed = this.sportsPracticedFormControl.value;
        athleteRow.athlete.externalCoachName = this.externalCoachNameFormControl.value;
        athleteRow.athlete.clubName = this.clubFormControl.value;
        athleteRow.athlete.description = this.athleteDescriptionFormControl.value;
        athleteRow.athlete.size = this.sizeFormControl.value;
        athleteRow.athlete.weight = this.weightFormControl.value;
        athleteRow.athlete.billingEmail = this.billingEmailFormControl.value;

        // user
        athleteRow.user.intro = this.athleteDescriptionFormControl.value;
        athleteRow.user.phoneNumber = this.phoneNumberFormControl.value;

        // adresse par défaut
        const defaultAddress: Address = new Address({
          id: this.defaultAddressId,
          athleteId: this.athleteRow.athlete.id,
          addressTypeId: AddressType.ID_DEFAULT,
          streetName: this.streetNameFormControl.value,
          buildingNumber: this.buildingNumberFormControl.value,
          addressAddition: this.addressAdditionFormControl.value,
          postcode: this.postcodeFormControl.value,
          city: this.cityFormControl.value
        });
        athleteRow.addresses.push(defaultAddress);

        return this._athletesProvider.saveAthlete(this.organization.id!, athleteRow).pipe(
          map((athlete) => {
            this.athleteRow = athlete;
            this.isLoading = false;
            this._changesGuard.unregisterChanges();

            if (this.currentUser.user?.id === this.user.id
              && (this.currentUser.user?.avatar !== this.user.avatar
                || this.currentUser.user?.langId !== this.user.langId)) {
              window.location.href = this._route.snapshot.url.join('/') + '?' + this.utils.randomString(8);
            } else {
              this._toaster.open(this._translater.instant('User_saved'), '', {duration: 5000});
              this.initNav();
            }
            return true;
          }),
        );
      }),
    ).subscribe({
      error: (error) => this.onError(error),
    });
  }

  public onFirstNameChange(): void {
    this.onFormChanged();
  }

  public onLastNameChange(): void {
    this.onFormChanged();
  }

  public onEmailChange(): void {
    this.onFormChanged();
    this.emailUnavailableError = false;
    this.emailFormControl.updateValueAndValidity();
  }

  public onBillingEmailChange(): void {
    this.onFormChanged();
  }

  public onAvatarImageChange(): void {
    this.onFormChanged();
  }

  public onIntroChange(): void {
    this.onFormChanged();
    if (this.user.intro && this.user.intro.length > this.DESCRIPTION_MAX_LENGTH) {
      this.user.intro = this.user.intro.substr(0, this.DESCRIPTION_MAX_LENGTH);
    }
  }

  public onLangChange(): void {
    this.onFormChanged();
  }

  public onUserLinksChanged(): void {
    this.onFormChanged();
  }

  public onGenderChange(): void {
    this.onFormChanged();
  }

  public onChangePasswordClicked(): void {
    const dialog: MatDialogRef<any> = this._dialoger.open(UsersChangePasswordDialogComponent, {
      data: {
        user: this.user
      }
    });
  }

  public isFormValid(): boolean {
    if (this.firstNameFormControl.invalid
      || this.lastNameFormControl.invalid
      || this.emailFormControl.invalid
      || this.billingEmailFormControl.invalid
      || this.genderFormControl.invalid
      || this.birthdayFormControl.invalid
      || this.postcodeFormControl.invalid
      || this.cityFormControl.invalid
      || this.streetNameFormControl.invalid
      || this.sizeFormControl.invalid
      || this.weightFormControl.invalid
    ) {
      return false;
    }

    for (let i = 0; i < this.userLinkFormControls.length; i++) {
      if (this.userLinkFormControls[i].invalid) {
        return false;
      }
    }

    return true;
  }

  private onFormChanged(): void {
    this._changesGuard.registerChanges(() => this.onSaveClicked());
  }

  private initPage(): void {
    this.loadData().subscribe({
      next: () => {
        this.initForms();
        this.initNav();
        this.isLoading = false;
        this._changeDetector.detectChanges();
      },
      error: (error) => this.onError(error)
    });
    this._changeDetector.detectChanges();
  }

  private initNav(): void {

    this.moreMenus = [
      { name: 'Change_password', link: '.', click: () => { this.onChangePasswordClicked(); } }
    ];

    this.createPageTitle();
  }

  private initForms(): void {

    this.genderFormControl.setValue(this.athleteRow.athlete.genderId);
    if (this.athleteRow.athlete.birthday) {
      this.birthdayFormControl.setValue(this.athleteRow.athlete.birthday);
    }
    if (this.athleteRow.addresses.length) {
      this.defaultAddressId = this.athleteRow.addresses[0].id!;
      this.postcodeFormControl.setValue(this.athleteRow.addresses[0].postcode);
      this.cityFormControl.setValue(this.athleteRow.addresses[0].city);
      this.streetNameFormControl.setValue(this.athleteRow.addresses[0].streetName);
      this.buildingNumberFormControl.setValue(this.athleteRow.addresses[0].buildingNumber);
      this.addressAdditionFormControl.setValue(this.athleteRow.addresses[0].addressAddition);
    }
    this.phoneNumberFormControl.setValue(this.athleteRow.user.phoneNumber);
    this.externalCoachNameFormControl.setValue(this.athleteRow.athlete.externalCoachName);
    this.sizeFormControl.setValue(this.athleteRow.athlete.size);
    this.weightFormControl.setValue(this.athleteRow.athlete.weight);
    this.sportsPracticedFormControl.setValue(this.athleteRow.athlete.sportsPracticed);
    this.contractTypeSelected = this.athleteRow.contractType.id!;
    this.clubFormControl.setValue(this.athleteRow.athlete.clubName);
    this.athleteDescriptionFormControl.setValue(this.athleteRow.athlete.description);

    this.userLinkFormControls = [];
    for (let i = 0; i < this.userLinksData.length; i++) {
      this.userLinkFormControls.push(new FormControl('', [
        (control: any) => {
          if (!control.value || !this.userLinksData[i].userLink.matchWith) {
            return null;
          }

          return control.value.startsWith(this.userLinksData[i].userLink.matchWith) ? null : { wrongLink: { valid: false } };
        }
      ]));
    }
  }

  private loadData(): Observable<any> {
    this.langs = this._langs.getLangs();
    const userId: number = this.parseUrl();

    if (!userId) {
      return of({ status: 404 });
    }

    return forkJoin({
      user: this.usersProvider.get(this.organization.id!, userId),
      userLinksData: this.usersProvider.getUserUserLinksData(userId),
      athleteData: this._athletesProvider.get(this.organization.id!, userId),
      availableGenders: this._athletesProvider.getGenders(),
      availableContractTypes: this._athletesProvider.getContractTypes()
    }).pipe(
      switchMap((results: any) => {
        if (!results.user) {
          return of({ status: 404 });
        }
        this.user = results.user;
        this.avatarImage = { current: this.user.avatar, new: null };
        this.userLinksData = results.userLinksData;
        this.availableGenders = results.availableGenders;
        this.availableContractTypes = results.availableContractTypes;

        return this._athletesProvider.getAthleteRow(this.organization.id!, results.athleteData.id).pipe(
          map((athleteRow: AthleteRow) => {
            this.athleteRow = athleteRow;
            return null;
          })
        );
      })
    );
  }

  private parseUrl(): number {
    return parseInt(this._route.snapshot.url[5].path, 10);
  }

  private createPageTitle() {

    const userName: string = this.user.firstName + ' ' + this.user.lastName;
    this._title.setTitle(userName + ' | ' + this._translater.instant('My_account') + ' | ' + this.conf.platformName);
    this.pageTitle = 'Account_settings';
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      const details: any = [];
      if (error._body) {
        let details: any = JSON.parse(error._body);
        if (details.errors) {
          details = details.errors;
        }
      }

      if (details['email']) {
        this.emailUnavailableError = true;
        this.emailFormControl.updateValueAndValidity();
      } else {
        if (error.error && error.error.errors) {
          if (error.error.errors.avatar) {
            this._toaster.open(this._translater.instant(error.error.errors.avatar[0]), '', {duration: 5000});
          } else {
            this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
          }
        } else {
          this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
        }
      }
    } else if (error.status === 404) {
      this._router.navigate(['404']);
    } else {
      this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }
  }
}
